import React, { memo } from 'react'
import styled from 'styled-components'
import { Box, theme } from 'pcln-design-system'
import { format } from 'date-fns'
import Day from '../Day/Day'
import DayNames from '../DayNames/DayNames'
import MonthName from '../MonthName/MonthName'
import { DayMapContext } from '../DayMapProvider'
import { PRICELINE_DATE_FORMAT } from '../constants/dates'
import { IMonthProps } from '../types'

const now = format(Date.now(), PRICELINE_DATE_FORMAT)

const MonthBox = styled(Box)`
  ${(props) => (props.theme ? props.theme.mediaQueries.md : theme.mediaQueries.md)} {
    vertical-align: top;
    display: inline-block;
  }
`

function Month({
  days,
  dayWithRoundedCorners,
  dayWrapperStyleOverride,
  didUseKeyNavigation,
  displayYear,
  endDate,
  highlightedDates,
  hoveredDate,
  isTouch,
  labels,
  locale,
  maxDate = now,
  maxSelectableDate,
  minDate = now,
  monthIndex,
  monthWidth = 380,
  showHoveredDates,
  startDate,
  year,
  getCalendarLegendId,
  onDayClicked,
  onDayHovered,
  setSelectedDayRef,
}: IMonthProps) {
  const mobileMonthBoxMargin = dayWithRoundedCorners ? 1 : 0

  return (
    <MonthBox width={[1, null, monthWidth]} p={[0, null, 3]} my={[mobileMonthBoxMargin, null, 0]}>
      <MonthName displayYear={displayYear} locale={locale} monthIndex={monthIndex} year={year} />
      <DayNames locale={locale} />
      <DayMapContext.Consumer>
        {(priceContext) => (
          <div>
            {days.map((fullDate, i) => (
              <Day
                ariaLabel={(labels && labels[i]) || ''}
                endDate={endDate}
                fullDate={fullDate}
                highlightedDates={highlightedDates}
                hoveredDate={hoveredDate}
                maxDate={maxDate}
                dayWrapperStyleOverride={dayWrapperStyleOverride}
                startDate={startDate}
                locale={locale}
                minDate={minDate}
                maxSelectableDate={maxSelectableDate}
                showHoveredDates={showHoveredDates}
                isTouch={isTouch}
                key={`datepicker-day-${fullDate || `${monthIndex}${i}`}`}
                customDayProps={priceContext && priceContext.dayMap ? priceContext.dayMap[fullDate] : null}
                renderDayFn={priceContext && priceContext.renderDayFn}
                didUseKeyNavigation={didUseKeyNavigation}
                dayWithRoundedCorners={dayWithRoundedCorners}
                onDayClicked={onDayClicked}
                onDayHovered={onDayHovered}
                getCalendarLegendId={getCalendarLegendId}
                setSelectedDayRef={setSelectedDayRef}
              />
            ))}
          </div>
        )}
      </DayMapContext.Consumer>
    </MonthBox>
  )
}

export function areEqual(prevProps, nextProps) {
  const { index, currentMonthIndex, isMobile, monthsToShow } = nextProps
  const isPastMonth = index < currentMonthIndex
  const isFutureMonth = index > currentMonthIndex + monthsToShow - 1

  if (!isMobile && (isPastMonth || isFutureMonth)) {
    return true
  }

  return prevProps === nextProps
}

export default memo(Month, areEqual)
